// apps/accounts/src/pages/materials/MaterialRequests.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Plus, Eye, Edit, Trash2, CheckCircle, XCircle, RefreshCw, Search,
  ChevronLeft, ChevronRight, X, Building2, Calendar, User
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';
import { useNavigate } from 'react-router-dom';

const MaterialRequests = () => {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const isAdmin = ['admin'].includes(role);
  const canApprove = ['admin', 'site_supervisor'].includes(role);
  const canIssue = ['admin', 'store_keeper'].includes(role);
  const canCreate = ['admin', 'site_engineer'].includes(role);

  const [requests, setRequests] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = 10;

  // ─── Create Modal State ──────────────────────────────────────
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    project: '',
    request_date: new Date().toISOString().split('T')[0],
    note: '',
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const fetchProjects = async () => {
    try {
      const records = await pb.collection('accounts_projects').getFullList({
        sort: 'name',
        requestKey: null,
      });
      setProjects(records);
    } catch (err) {
      if (err.isAbort) return;
      console.warn('Could not fetch projects');
    }
  };

  const fetchRequests = async () => {
    try {
      const filters = [];
      if (search) filters.push(`(project.name ~ "${search}" || requested_by.name ~ "${search}")`);
      if (filterStatus !== 'All') filters.push(`status = "${filterStatus}"`);
      const filter = filters.join(' && ');
      const result = await pb.collection('material_requests').getList(page, perPage, {
        filter: filter || undefined,
        sort: '-request_date',
        expand: 'project,requested_by,approved_by',
        requestKey: null,
      });
      setRequests(result.items);
      setTotalPages(result.totalPages);
    } catch (err) {
      if (err.isAbort) return;
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchRequests();
  }, [search, filterStatus, page]);

  // ─── Handlers ──────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this request?')) return;
    try {
      await pb.collection('material_requests').delete(id);
      toast.success('Deleted');
      fetchRequests();
    } catch { toast.error('Delete failed'); }
  };

  const handleApprove = async (id) => {
    try {
      await pb.collection('material_requests').update(id, {
        status: 'Approved',
        approved_by: user.id,
        approved_date: new Date().toISOString(),
      });
      toast.success('Request approved');
      fetchRequests();
    } catch { toast.error('Approval failed'); }
  };

  const handleIssue = async (id) => {
    try {
      const req = await pb.collection('material_requests').getOne(id, { expand: 'items.material' });
      for (const item of req.expand?.items || []) {
        if (item.quantity_issued > 0) {
          const mat = await pb.collection('materials').getOne(item.material);
          const newStock = Math.max(0, (mat.current_stock || 0) - item.quantity_issued);
          const status = newStock <= 0 ? 'Out of Stock' : (newStock < mat.min_stock ? 'Low Stock' : 'Available');
          await pb.collection('materials').update(item.material, { current_stock: newStock, status });
          await pb.collection('material_transactions').create({
            material: item.material,
            type: 'issue',
            quantity: item.quantity_issued,
            date: new Date().toISOString().split('T')[0],
            note: 'Issued from request',
            user: user.id,
          });
        }
      }
      await pb.collection('material_requests').update(id, {
        status: 'Issued',
        issued_date: new Date().toISOString(),
      });
      toast.success('Request issued');
      fetchRequests();
    } catch { toast.error('Issue failed'); }
  };

  // ─── Create Modal Handlers ──────────────────────────────────
  const openCreateModal = () => {
    setFormData({
      project: '',
      request_date: new Date().toISOString().split('T')[0],
      note: '',
    });
    setFormError('');
    setShowCreateModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    try {
      const data = {
        project: formData.project,
        requested_by: user.id,
        request_date: formData.request_date || new Date().toISOString().split('T')[0],
        status: 'Draft',
        note: formData.note || null,
      };
      const record = await pb.collection('material_requests').create(data);
      toast.success('Request created');
      setShowCreateModal(false);
      // Redirect to the new request detail page to add items
      navigate(`/materials/requests/${record.id}`);
    } catch (err) {
      let msg = err.message || 'Failed';
      if (err.data?.data) {
        const fields = Object.entries(err.data.data).map(([k, v]) => `${k}: ${v.message}`).join(', ');
        msg += ` (${fields})`;
      }
      setFormError(msg);
      setFormLoading(false);
    }
  };

  // ─── Helpers ──────────────────────────────────────────────
  const statusBadge = (status) => {
    const colors = {
      Draft: 'bg-slate-100 text-slate-700',
      Pending: 'bg-amber-100 text-amber-700',
      Approved: 'bg-blue-100 text-blue-700',
      Issued: 'bg-green-100 text-green-700',
      Cancelled: 'bg-red-100 text-red-700',
    };
    return <Badge className={colors[status] || 'bg-slate-100'}>{status}</Badge>;
  };

  if (loading) return <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400" /></div>;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Material Requests</h1>
          <p className="text-sm text-slate-500">Request materials for projects</p>
        </div>
        {canCreate && (
          <Button onClick={openCreateModal} className="bg-amber-500 hover:bg-amber-600 text-white">
            <Plus className="w-4 h-4 mr-1" /> New Request
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-4 bg-white p-4 rounded-xl shadow-sm">
        <Input
          placeholder="Search project or requester..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[200px]"
        />
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border rounded-lg"
        >
          <option value="All">All Status</option>
          <option value="Draft">Draft</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Issued">Issued</option>
          <option value="Cancelled">Cancelled</option>
        </select>
        <Button variant="outline" onClick={() => { setSearch(''); setFilterStatus('All'); }}>
          Clear
        </Button>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Project</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Requested By</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Date</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-600">Status</th>
                <th className="px-4 py-3 text-center font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {requests.length === 0 ? (
                <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-500">No requests</td></tr>
              ) : (
                requests.map(r => (
                  <tr key={r.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3">{r.expand?.project?.name || 'N/A'}</td>
                    <td className="px-4 py-3">{r.expand?.requested_by?.name || 'N/A'}</td>
                    <td className="px-4 py-3">{new Date(r.request_date).toLocaleDateString()}</td>
                    <td className="px-4 py-3">{statusBadge(r.status)}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center gap-1">
                        <button
                          onClick={() => navigate(`/materials/requests/${r.id}`)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {r.status === 'Pending' && canApprove && (
                          <button onClick={() => handleApprove(r.id)} className="p-1.5 text-green-600 hover:bg-green-50 rounded">
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        {r.status === 'Approved' && canIssue && (
                          <button onClick={() => handleIssue(r.id)} className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded">
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        {isAdmin && (
                          <button onClick={() => handleDelete(r.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-4">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 border rounded-lg disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4 inline" /> Previous
          </button>
          <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1.5 border rounded-lg disabled:opacity-50"
          >
            Next <ChevronRight className="w-4 h-4 inline" />
          </button>
        </div>
      )}

      {/* ─── Create Modal ────────────────────────────────────── */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">New Material Request</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            {formError && <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 border border-red-200">{formError}</div>}
            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Project *</label>
                <select
                  name="project"
                  value={formData.project}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400"
                  required
                >
                  <option value="">Select Project</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Request Date</label>
                <Input
                  type="date"
                  name="request_date"
                  value={formData.request_date}
                  onChange={handleFormChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Note (optional)</label>
                <Input
                  name="note"
                  value={formData.note}
                  onChange={handleFormChange}
                  placeholder="Any special instructions"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-600" disabled={formLoading}>
                  {formLoading ? 'Creating...' : 'Create Request'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialRequests;